import { KatFileUpload, KatInput, KatRadiobuttonGroup } from '@amzn/katal-react';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { AssetInputProps } from 'src/components/pages/campaignAutomation/input/assets';
import { StateConstants, TranslatableConstants } from 'src/constants';
import { logger } from 'src/logger';
import { AssetStatus, InputType } from 'src/types';
import { validateUrl } from 'src/utils/assetInput/assetInputUtils';

/**
 * Return type for the useAssetInput hook
 */
interface UseAssetInputReturn {
  /** Current selected input type (URL or File Upload) */
  selectedType: InputType;

  /** Current URL input value */
  url: string;

  /** Whether to show the confirmation dialog */
  showConfirmDialog: boolean;

  /** Input type that will be set after confirmation */
  pendingInputType: InputType | null;

  /** Generated unique name for the radio group */
  radioGroupName: string;

  /** Whether the current URL input is valid and not previously fetched */
  isValidUrlInput: boolean;

  /** Whether there is an existing asset (completed or pending upload) */
  hasExistingAsset: boolean;

  /**
   * Handles URL input changes
   * @param event - Katal input change event
   */
  handleUrlChange: (event: KatInput.ChangeEvent) => void;

  /**
   * Handles URL fetch action
   * Shows confirmation dialog if there's an existing asset
   */
  handleUrlFetch: () => void;

  /**
   * Handles file attachment events
   * @param event - Katal file attached event
   */
  handleFileAttached: (event: KatFileUpload.FilesAttachedEvent) => void;

  /**
   * Handles file replacement events
   * @param event - Katal file replaced event
   */
  handleFileReplaced: (event: KatFileUpload.FilesReplacedEvent) => void;

  /**
   * Handles file attachment error events
   * @param event - Katal file attachment error event
   */
  handleFileAttachedError: (event: KatFileUpload.FilesAttachedErrorEvent) => void;

  /**
   * Handles input type radio button changes
   * Shows confirmation dialog if there's an existing asset
   * @param event - Katal radio button change event
   */
  handleRadioChange: (event: KatRadiobuttonGroup.ChangeEvent) => void;

  /**
   * Confirms the pending input type or URL change
   */
  handleConfirmTypeChange: () => void;

  /**
   * Cancels the pending input type or URL change
   */
  handleCancelTypeChange: () => void;

  /**
   * Gets the appropriate confirmation message based on the current context
   * @returns Message to display in the confirmation dialog
   */
  getConfirmationMessage: () => string;
}

/**
 * Hook for managing asset input state and logic.
 * Handles both file uploads and URL inputs with confirmation dialogs
 * for replacing existing assets.
 *
 * @param props - Asset input configuration
 * @param props.label - Label for the input (used for generating unique IDs)
 * @param props.assetInputHandler - Handler for asset-related actions
 * @param props.status - Current status of the asset
 *
 * @example
 * // Basic usage
 * const {
 *   selectedType,
 *   url,
 *   handleUrlChange,
 *   handleUrlFetch,
 *   handleFileAttached
 * } = useAssetInput({
 *   label: 'Background Image',
 *   assetInputHandler: backgroundImageHandler,
 *   status: AssetStatus.PENDING_INPUT
 * });
 *
 * @example
 * // Handling URL input
 * const {
 *   url,
 *   isValidUrlInput,
 *   handleUrlChange,
 *   handleUrlFetch
 * } = useAssetInput(props);
 *
 * return (
 *   <UrlInput
 *     url={url}
 *     isValidUrl={isValidUrlInput}
 *     onChange={handleUrlChange}
 *     onFetch={handleUrlFetch}
 *   />
 * );
 */
export const useAssetInput = ({
  label,
  assetInputHandler,
  status,
}: Pick<AssetInputProps, 'label' | 'assetInputHandler' | 'status'>): UseAssetInputReturn => {
  // State
  const [selectedType, setSelectedType] = useState<InputType>(InputType.UPLOAD);
  const [url, setUrl] = useState<string>(StateConstants.EMPTY_STRING);
  const [showConfirmDialog, setShowConfirmDialog] = useState(false);
  const [pendingInputType, setPendingInputType] = useState<InputType | null>(null);
  const [pendingUrl, setPendingUrl] = useState<string>(StateConstants.EMPTY_STRING);
  const [confirmationContext, setConfirmationContext] = useState<'input-type' | 'url'>(
    'input-type',
  );

  // Refs for tracking validation and URL fetch state
  const lastFetchedUrl = useRef<string>(StateConstants.EMPTY_STRING);
  const validationErrorRef = useRef<boolean>(false);

  // Generate unique radio group name from label
  const radioGroupName = useMemo(
    () => `asset-input__${label.toLowerCase().replace(/\s+/g, '-')}`,
    [label],
  );

  // Check if URL is valid and not previously fetched
  const isValidUrlInput = useMemo(
    () => validateUrl(url) && url !== lastFetchedUrl.current && !validationErrorRef.current,
    [url],
  );

  // Check if there's an existing asset
  const hasExistingAsset = useMemo(
    () => Boolean(status && [AssetStatus.COMPLETED, AssetStatus.PENDING_UPLOAD].includes(status)),
    [status],
  );

  /**
   * Clears all input state and errors
   * This is a core function used by other handlers to reset the input state
   */
  const clearInputState = useCallback(() => {
    setUrl(StateConstants.EMPTY_STRING);
    lastFetchedUrl.current = StateConstants.EMPTY_STRING;
    assetInputHandler.clearError();
    assetInputHandler.handleFileRemoved();
  }, [assetInputHandler]);

  /**
   * Updates the input type and clears existing state
   * @param newType - The new input type to set
   */
  const updateInputType = useCallback(
    (newType: InputType) => {
      clearInputState();
      setSelectedType(newType);
    },
    [clearInputState],
  );

  /**
   * Processes a URL fetch operation
   * Handles the URL through the asset handler and updates state
   * @param urlToFetch - The URL to process
   */
  const processUrlFetch = useCallback(
    (urlToFetch: string) => {
      assetInputHandler.handleUrl(urlToFetch);
      lastFetchedUrl.current = urlToFetch;
      setUrl(StateConstants.EMPTY_STRING);
    },
    [assetInputHandler],
  );

  /**
   * Handles URL input changes
   * @param event - Katal input change event
   */
  const handleUrlChange = useCallback((event: KatInput.ChangeEvent) => {
    setUrl(event.currentTarget.value);
  }, []);

  /**
   * Handles URL fetch action
   * Shows confirmation dialog if there's an existing asset
   */
  const handleUrlFetch = useCallback(() => {
    if (isValidUrlInput) {
      if (hasExistingAsset) {
        setPendingUrl(url);
        setConfirmationContext('url');
        setShowConfirmDialog(true);
      } else {
        processUrlFetch(url);
      }
    }
  }, [url, isValidUrlInput, hasExistingAsset, processUrlFetch]);

  /**
   * Handles file attachment events
   * Skips if there was a validation error
   * @param event - Katal file attached event
   */
  const handleFileAttached = useCallback(
    (event: KatFileUpload.FilesAttachedEvent) => {
      if (validationErrorRef.current) {
        validationErrorRef.current = false;
        return;
      }
      assetInputHandler.handleFileAttached(event);
    },
    [assetInputHandler],
  );

  /**
   * Handles file replacement events
   * Skips if there was a validation error
   * @param event - Katal file replaced event
   */
  const handleFileReplaced = useCallback(
    (event: KatFileUpload.FilesReplacedEvent) => {
      if (validationErrorRef.current) {
        validationErrorRef.current = false;
        return;
      }
      assetInputHandler.handleFileReplaced(event);
    },
    [assetInputHandler],
  );

  /**
   * Handles file attachment error events
   * Sets validation error flag and delegates to handler
   * @param event - Katal file attachment error event
   */
  const handleFileAttachedError = useCallback(
    (event: KatFileUpload.FilesAttachedErrorEvent) => {
      validationErrorRef.current = true;
      assetInputHandler.handleFileAttachedError(event);
    },
    [assetInputHandler],
  );

  /**
   * Handles input type radio button changes
   * Shows confirmation dialog if there's an existing asset
   * @param event - Katal radio button change event
   */
  const handleRadioChange = useCallback(
    (event: KatRadiobuttonGroup.ChangeEvent) => {
      event.preventDefault();
      const value = event?.detail?.value;
      if (
        value &&
        typeof value === 'string' &&
        Object.values(InputType).includes(value as InputType)
      ) {
        if (hasExistingAsset) {
          setPendingInputType(value as InputType);
          setConfirmationContext('input-type');
          setShowConfirmDialog(true);
          return false;
        } else {
          updateInputType(value as InputType);
          return true;
        }
      }
      return false;
    },
    [hasExistingAsset, updateInputType],
  );

  /**
   * Confirms the pending input type or URL change
   * Handles both input type and URL confirmation contexts
   */
  const handleConfirmTypeChange = useCallback(() => {
    if (pendingInputType) {
      updateInputType(pendingInputType);
      setPendingInputType(null);
      setShowConfirmDialog(false);
    } else if (pendingUrl) {
      processUrlFetch(pendingUrl);
      setPendingUrl(StateConstants.EMPTY_STRING);
      setShowConfirmDialog(false);
    }
  }, [pendingInputType, pendingUrl, updateInputType, processUrlFetch]);

  /**
   * Cancels the pending input type or URL change
   * Resets all pending state
   */
  const handleCancelTypeChange = useCallback(() => {
    setShowConfirmDialog(false);
    setPendingInputType(null);
    setPendingUrl(StateConstants.EMPTY_STRING);
  }, []);

  /**
   * Gets the appropriate confirmation message based on context
   * @returns Message to display in the confirmation dialog
   */
  const getConfirmationMessage = useCallback(() => {
    return confirmationContext === 'input-type'
      ? TranslatableConstants.INPUT_TYPE_CONFIRMATION_MESSAGE
      : TranslatableConstants.URL_CONFIRMATION_MESSAGE;
  }, [confirmationContext]);

  // Log status changes for debugging
  useEffect(() => {
    logger.debug(`AssetStatus changed to ${status}`);
  }, [status]);

  return {
    // State
    selectedType,
    url,
    showConfirmDialog,
    pendingInputType,

    // Computed values
    radioGroupName,
    isValidUrlInput,
    hasExistingAsset,

    // Handlers
    handleUrlChange,
    handleUrlFetch,
    handleFileAttached,
    handleFileReplaced,
    handleFileAttachedError,
    handleRadioChange,
    handleConfirmTypeChange,
    handleCancelTypeChange,
    getConfirmationMessage,
  };
};
